import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send, Cloud, FileText, UserPlus, Activity } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import StatusBadge, { STATUS_OPTIONS } from "@/components/admin/StatusBadge";
import { addNote, getLead, patchLead, syncLeadToDynamics } from "@/lib/adminApi";

const STATUS_LABELS = { new: "New", contacted: "Contacted", qualified: "Qualified", won: "Won", lost: "Lost" };

function ts(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function ActivityIcon({ type }) {
  switch (type) {
    case "created":               return <UserPlus size={12} />;
    case "status_change":         return <Activity size={12} />;
    case "note_added":            return <FileText size={12} />;
    case "duplicate_submission":  return <UserPlus size={12} />;
    case "synced":                return <Cloud size={12} />;
    default:                      return <Activity size={12} />;
  }
}

function describeActivity(a) {
  if (a.type === "created") return `Lead captured via ${a.to || "form"}`;
  if (a.type === "status_change") return `Status changed: ${a.from || "—"} → ${a.to}`;
  if (a.type === "note_added") return `Added a note${a.meta?.preview ? `: "${a.meta.preview}"` : ""}`;
  if (a.type === "duplicate_submission") return `Duplicate submission via ${a.to}`;
  if (a.type === "assigned") return `Assigned: ${a.from || "—"} → ${a.to || "—"}`;
  if (a.type === "synced") return `Synced to Dynamics${a.to ? ` (id: ${a.to})` : ""}`;
  return a.type;
}

export default function AdminLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [pendingLost, setPendingLost] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const l = await getLead(id);
      setLead(l);
      document.title = `${l.name} · Admin · RentAnything.ai`;
    } catch {
      toast.error("Couldn't load that lead.");
      navigate("/admin", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const applyStatus = async (newStatus) => {
    setSavingStatus(true);
    try {
      const updated = await patchLead(id, { status: newStatus });
      setLead(updated);
      toast.success(`Status → ${STATUS_LABELS[newStatus] || newStatus}`);
    } catch {
      toast.error("Could not update status.");
    } finally {
      setSavingStatus(false);
    }
  };

  const onStatusChange = (v) => {
    if (v === "lost" && lead?.status !== "lost") {
      setPendingLost(true);
      return;
    }
    applyStatus(v);
  };

  const onAddNote = async (e) => {
    e.preventDefault();
    if (!noteBody.trim()) return;
    setAddingNote(true);
    try {
      await addNote(id, noteBody.trim());
      setNoteBody("");
      toast.success("Note added");
      await load();
    } catch {
      toast.error("Could not add note.");
    } finally {
      setAddingNote(false);
    }
  };

  const onSyncClick = async () => {
    const tid = toast.loading("Syncing to Dynamics…");
    try {
      const res = await syncLeadToDynamics(id);
      toast.dismiss(tid);
      if (res.ok) {
        toast.success(`Synced to Dynamics${res.dynamics_lead_id ? ` (id ${String(res.dynamics_lead_id).slice(0, 8)}…)` : ""}`);
        await load();
      } else if (res.status === "disabled") {
        toast.info("Dynamics integration is disabled (DYNAMICS_ENABLED=false).");
      } else {
        toast.error(`Sync failed: ${res.error || res.status || "unknown"}`);
        await load();
      }
    } catch (e) {
      toast.dismiss(tid);
      toast.error(`Sync failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  if (loading || !lead) {
    return (
      <div className="flex items-center justify-center py-20 text-ra-ink/55">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-lead-detail">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ra-ink/70 hover:text-ra-ocean" data-testid="admin-lead-back-link">
        <ArrowLeft size={14} /> All leads
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-ra-ink">{lead.name}</h1>
          <p className="mt-1 text-sm text-ra-ink/65">
            {lead.business_name || "—"} · <span className="text-ra-ocean">{lead.email}</span>
            {lead.phone ? ` · ${lead.phone}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={onSyncClick}
          className="ra-btn-ghost"
          data-testid="admin-sync-dynamics-btn"
        >
          <Cloud size={14} /> Sync to Dynamics
        </button>
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        {/* LEFT: contact card + activity */}
        <div className="space-y-5">
          <section className="rounded-2xl border border-ra-line bg-white p-6 shadow-card" data-testid="admin-lead-contact">
            <p className="text-xs font-semibold uppercase tracking-wider text-ra-ink/55">Contact</p>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <Field label="Name" value={lead.name} />
              <Field label="Business" value={lead.business_name} />
              <Field label="Email" value={lead.email} />
              <Field label="Phone" value={lead.phone} />
              <Field label="Vertical" value={lead.vertical} capitalize />
              <Field label="Units" value={lead.units} />
              <Field label="Source" value={(lead.source || "").replace(/_/g, " ")} capitalize />
              <Field label="Assigned to" value={lead.assigned_to} />
              <Field label="Created" value={ts(lead.created_at)} />
              <Field label="Updated" value={ts(lead.updated_at)} />
              <Field label="Sync status" value={lead.dynamics_sync_status} />
              <Field label="Dynamics ID" value={lead.dynamics_lead_id || "—"} />
            </dl>
            {lead.message && (
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-ra-ink/55">Message</p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ra-ink/85">{lead.message}</p>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-ra-line bg-white p-6 shadow-card" data-testid="admin-lead-activity">
            <p className="text-xs font-semibold uppercase tracking-wider text-ra-ink/55">Activity timeline</p>
            <ol className="mt-4 space-y-3">
              {(lead.activity || []).slice().reverse().map((a) => (
                <li key={a.id} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ra-ocean/15 text-ra-ocean">
                    <ActivityIcon type={a.type} />
                  </span>
                  <div>
                    <p className="text-ra-ink">{describeActivity(a)}</p>
                    <p className="text-[11px] text-ra-ink/50">{a.by || "system"} · {ts(a.at)}</p>
                  </div>
                </li>
              ))}
              {(!lead.activity || lead.activity.length === 0) && (
                <li className="text-sm text-ra-ink/55">No activity yet.</li>
              )}
            </ol>
          </section>
        </div>

        {/* RIGHT: status + notes */}
        <div className="space-y-5">
          <section className="rounded-2xl border border-ra-line bg-white p-6 shadow-card" data-testid="admin-lead-status-card">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-ra-ink/55">Status</p>
              <StatusBadge value={lead.status} />
            </div>
            <div className="mt-4">
              <Select value={lead.status} onValueChange={onStatusChange} disabled={savingStatus}>
                <SelectTrigger data-testid="admin-status-select">
                  <SelectValue placeholder="Set status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`admin-status-option-${s}`}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          <section className="rounded-2xl border border-ra-line bg-white p-6 shadow-card" data-testid="admin-lead-notes">
            <p className="text-xs font-semibold uppercase tracking-wider text-ra-ink/55">Notes</p>
            <form onSubmit={onAddNote} className="mt-3">
              <Textarea
                rows={3}
                placeholder="Add an internal note…"
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                data-testid="admin-note-input"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={addingNote || !noteBody.trim()}
                  className="ra-btn-primary"
                  data-testid="admin-note-submit"
                >
                  {addingNote ? <><Loader2 size={14} className="animate-spin" /> Saving</> : <><Send size={14} /> Add note</>}
                </button>
              </div>
            </form>

            <ul className="mt-5 space-y-3">
              {(lead.notes || []).slice().reverse().map((n) => (
                <li key={n.id} className="rounded-xl border border-ra-line bg-ra-paper p-3" data-testid={`admin-note-item-${n.id}`}>
                  <p className="text-sm text-ra-ink/90 whitespace-pre-wrap">{n.body}</p>
                  <p className="mt-1.5 text-[11px] text-ra-ink/55">{n.created_by} · {ts(n.created_at)}</p>
                </li>
              ))}
              {(!lead.notes || lead.notes.length === 0) && (
                <li className="text-sm text-ra-ink/55">No notes yet.</li>
              )}
            </ul>
          </section>
        </div>
      </div>

      <AlertDialog open={pendingLost} onOpenChange={setPendingLost}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this lead as Lost?</AlertDialogTitle>
            <AlertDialogDescription>
              {lead.name} ({lead.business_name || lead.email}) will be moved to the Lost stage. This is an audited change — you can always set it back, but the activity log will record both moves.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-confirm-lost-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setPendingLost(false); applyStatus("lost"); }}
              data-testid="admin-confirm-lost-ok"
            >
              Mark as Lost
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value, capitalize }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-ra-ink/50">{label}</dt>
      <dd className={`mt-0.5 text-sm text-ra-ink ${capitalize ? "capitalize" : ""}`}>{value || "—"}</dd>
    </div>
  );
}
